import { useEffect, useState } from 'react';
import { routeAliases, routes, supportedRoutes } from '../routes/routes';

function normalizePath(path) {
  const pathValue = path || routes.home;
  const pathname = pathValue.split('?')[0].split('#')[0] || routes.home;
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;

  return normalizedPath.length > 1 ? normalizedPath.replace(/\/+$/, '') : normalizedPath;
}

function resolvePath(path) {
  const normalizedPath = normalizePath(path);

  return routeAliases[normalizedPath] || normalizedPath;
}

function getLegacyHashRoute() {
  const rawHash = window.location.hash.replace(/^#/, '');

  if (!rawHash || !rawHash.startsWith('/')) return null;

  const queryIndex = rawHash.indexOf('?');
  const rawPath = queryIndex >= 0 ? rawHash.slice(0, queryIndex) : rawHash;
  const rawSearch = queryIndex >= 0 ? rawHash.slice(queryIndex) : '';
  const resolvedPath = resolvePath(rawPath);

  if (!supportedRoutes.has(resolvedPath)) return null;

  return {
    path: resolvedPath,
    url: `${resolvedPath}${rawSearch}`,
  };
}

export function getCurrentPath() {
  const legacyHashRoute = getLegacyHashRoute();

  if (legacyHashRoute) return legacyHashRoute.path;

  const resolvedPath = resolvePath(window.location.pathname);

  return supportedRoutes.has(resolvedPath) ? resolvedPath : routes.home;
}

export function useCurrentPath() {
  const [path, setPath] = useState(getCurrentPath);

  useEffect(() => {
    const updatePath = () => {
      const legacyHashRoute = getLegacyHashRoute();

      if (legacyHashRoute) {
        window.history.replaceState(window.history.state, '', legacyHashRoute.url);
      } else {
        const normalizedPath = normalizePath(window.location.pathname);
        const resolvedPath = resolvePath(normalizedPath);

        if (supportedRoutes.has(resolvedPath) && resolvedPath !== normalizedPath) {
          window.history.replaceState(
            window.history.state,
            '',
            `${resolvedPath}${window.location.search}${window.location.hash}`,
          );
        }
      }

      setPath(getCurrentPath());
    };

    const handleDocumentClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      const anchor = event.target.closest?.('a[href]');

      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;
      if (anchor.getAttribute('href')?.startsWith('#')) return;

      const nextUrl = new URL(anchor.href, window.location.href);

      if (nextUrl.origin !== window.location.origin) return;

      const resolvedPath = resolvePath(nextUrl.pathname);

      if (!supportedRoutes.has(resolvedPath)) return;

      const nextLocation = `${resolvedPath}${nextUrl.search}${nextUrl.hash}`;

      event.preventDefault();
      window.history.pushState({}, '', nextLocation);
      window.dispatchEvent(new Event('popstate'));
    };

    updatePath();
    window.addEventListener('popstate', updatePath);
    window.addEventListener('hashchange', updatePath);
    document.addEventListener('click', handleDocumentClick);

    return () => {
      window.removeEventListener('popstate', updatePath);
      window.removeEventListener('hashchange', updatePath);
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return path;
}
